import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from "@angular/core"; import { Location } from "@angular/common";

import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { ReponseBase } from './reponse-base';
import { EmployeEntite } from '../entites/employe';
import { RoleEntite } from '../entites/role';
import { RequeteModifierRoles } from './requetes/requete-modifier-roles';
import { BaseService } from './base.service';
import { Message, MessageService } from 'primeng/api';
import { Emprunt } from '../entites/emprunt';
import { Emplacement } from '../entites/emplacement';
import { MembreEmplacement } from '../entites/membre-emplacement';

@Injectable({ providedIn: 'root' })
export class EmployeService extends BaseService {

  private obtenirListeUrl = environment.apiBaseUrl + 'employe';
  private obtenirUrl = environment.apiBaseUrl + 'employe/{id}';
  private ajouterUrl = environment.apiBaseUrl + 'employe';
  private modifierUrl = environment.apiBaseUrl + 'employe/modifier';
  private supprimerUrl = environment.apiBaseUrl + 'employe/{id}';
  private obtenirRolesUrl = environment.apiBaseUrl + 'employe/roles';
  private obtenirRolesEmployeUrl = environment.apiBaseUrl + 'employe/{id}/roles';
  private modifierRolesUrl = environment.apiBaseUrl + 'employe/roles';
  private obtenirEmpruntsUrl = environment.apiBaseUrl + 'employe/{employeId}/emprunts';
  private obtenirEmplacementsUrl = environment.apiBaseUrl + 'employe/{employeId}/emplacements';
  private activerEmployeUrl = environment.apiBaseUrl + 'employe/{id}/activation';
  private desarchiverEmployeUrl = environment.apiBaseUrl + 'employe/{employeId}/dearchiver';
  private ajouterRoleUrl = environment.apiBaseUrl + 'employe/roles/ajouter';
  private modifierTachesRoleUrl = environment.apiBaseUrl + 'employe/roles/{roleId}/taches';
  private supprimerRoleUrl = environment.apiBaseUrl + 'employe/roles/{roleId}';
  private obtenirEmplacementCourantUrl = environment.apiBaseUrl + 'employe/emplacementcourant';

  public httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  constructor(
    private http: HttpClient,
    public router: Router,
    public messageService: MessageService
  ) {
    super(messageService, router);
  }

  public ObtenirListe(): Promise<ReponseBase<EmployeEntite[]>> {
    var p = this.http.get<ReponseBase<EmployeEntite[]>>(this.obtenirListeUrl)
      .toPromise();

      p.catch(() => this.router.navigateByUrl('error'));

      return p;
  }

  public Obtenir(id: number): Promise<ReponseBase<EmployeEntite>> {
    var p = this.http.get<ReponseBase<EmployeEntite>>(this.obtenirUrl.replace('{id}', id.toString()))
      .toPromise();

      p.catch(() => this.router.navigateByUrl('error'));

      return p;
  }

  public ObtenirEmprunts(employeId: number): Observable<Emprunt[]> {
    return this.http.get<Emprunt[]>(this.obtenirEmpruntsUrl.replace('{employeId}', employeId.toString()));
  }

  public ObtenirEmplacements(employeId: number): Observable<MembreEmplacement[]> {
    return this.http.get<MembreEmplacement[]>(this.obtenirEmplacementsUrl.replace('{employeId}', employeId.toString()));
  }

  public Ajouter(employe: EmployeEntite): Promise<ReponseBase<EmployeEntite>> {
    var p = this.http.post<ReponseBase<EmployeEntite>>(this.ajouterUrl, JSON.stringify(employe), this.httpOptions)
      .toPromise();

      p.catch(() => this.router.navigateByUrl('error'));

      return p;
  }

  public ActiverEmploye(id: number): Observable<Message> {
    return this.http.post<Message>(this.activerEmployeUrl.replace('{id}', id.toString()), this.httpOptions)
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  public Modifier(employe: EmployeEntite): Promise<ReponseBase<EmployeEntite>> {
    var p = this.http.post<ReponseBase<EmployeEntite>>(this.modifierUrl, JSON.stringify(employe), this.httpOptions)
      .toPromise();

      p.catch(() => this.router.navigateByUrl('error'));

      return p;
  }

  public Supprimer(id: number): Observable<Message> {
    return this.http.delete<Message>(this.supprimerUrl.replace('{id}', id.toString()))
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  public ObtenirRoles(): Observable<RoleEntite[]> {
    return this.http.get<RoleEntite[]>(this.obtenirRolesUrl)
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  public ObtenirRolesEmploye(id: number): Observable<RoleEntite[]> {
    return this.http.get<RoleEntite[]>(this.obtenirRolesEmployeUrl.replace('{id}', id.toString()))
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  public ModifierRoles(request: RequeteModifierRoles): Observable<boolean> {
    return this.http.post<boolean>(this.modifierRolesUrl, JSON.stringify(request), this.httpOptions)
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  public DesarchiverEmploye(employeId: number): Observable<Message> {
    return this.http.post<Message>(this.desarchiverEmployeUrl.replace('{employeId}', employeId.toString()), this.httpOptions)
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

/**
   * Retrieves the current emplacement for a list of employees
   * @param ids Array of employee IDs to lookup
   * @returns Observable of MembreEmplacement array with emplacement info for each employee
   */
  public ObtenirEmplacementCourant(ids: number[]): Observable<MembreEmplacement[]> {
    return this.http.get<MembreEmplacement[]>(this.obtenirEmplacementCourantUrl, { params: { ids: ids.join(',') } });
  }

  /**
   * Ajoute un nouveau rôle
   * @param role Données du rôle à créer
   * @returns Le rôle créé
   */
  public ajouterRole(role: RoleEntite): Observable<RoleEntite> {
    return this.http.post<RoleEntite>(this.ajouterRoleUrl, JSON.stringify(role), this.httpOptions)
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Modifie les tâches associées à un rôle spécifique
   * @param roleId ID du rôle à modifier
   * @param tacheIds Liste des IDs des tâches à associer au rôle
   * @returns Retourne true si la modification a réussi
   */
  public modifierTachesRole(roleId: number, tacheIds: number[]): Observable<boolean> {
    return this.http.post<boolean>(this.modifierTachesRoleUrl.replace('{roleId}', roleId.toString()), 
      JSON.stringify(tacheIds), this.httpOptions)
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Supprime un rôle (sauf SIHC_Administrateur)
   * @param roleId ID du rôle à supprimer
   * @returns Message de confirmation
   */
  public supprimerRole(roleId: number): Observable<Message> {
    return this.http.delete<Message>(this.supprimerRoleUrl.replace('{roleId}', roleId.toString()))
      .pipe(
        catchError((error) => this.handleError(error))
      );
  }
}
